import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { addKiosk, deleteKiosk } from 'api/Kiosk';

import { formatDate } from 'lib/helpers/dateUtils';
import { cn } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import ColorCircle from 'components/practice/Settings/components/Kiosks/components/ColorCircle';
import EditKioskModal from 'components/practice/Settings/components/Kiosks/lib/Edit/EditKioskModal';
import Dot from 'components/shared/Dot/Dot';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';

import { availableKioskState } from './lib/helpers';

const KioskCard = ({ kiosk }) => {
  const [editKiosk, setEditKiosk] = useState(null);
  const [deleteKioskModal, setDeleteKioskModal] = useState(null);
  const { id: practiceId } = useParams();
  const navigate = useNavigate();

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const { mutate: deleteKioskHandler } = useCustomMutation({
    mutationFn: () => deleteKiosk(navigate, { practiceId, id: deleteKioskModal }),
    successMessage: 'Successfully deleted kiosk!',
    invalidateQueryKey: 'kiosks',
    onSuccess: () => {
      setDeleteKioskModal(null);
    }
  });

  const { mutate: toggleKioskStatusHandler } = useCustomMutation({
    mutationFn: () =>
      addKiosk(navigate, {
        practiceId,
        values: { status: kiosk.status === 'active' ? 'inactive' : 'active' },
        kioskId: kiosk.id
      }),
    successMessage: 'Successfully updated kiosk status!',
    invalidateQueryKey: 'kiosks'
  });

  const kioskState = availableKioskState[kiosk.state] || availableKioskState.idle;

  return (
    <>
      <div
        className="flex h-fit flex-col items-center gap-4 rounded-2xl border border-solid border-neutral-300 p-4"
        style={{ backgroundColor: hexToRgba(kiosk.color, 0.05) }}>
        <div
          className="flex h-[200px] w-full flex-col rounded-2xl border border-solid border-neutral-300 bg-opacity-30 px-5 py-6 shadow-inner"
          style={{ backgroundColor: hexToRgba(kiosk.color, 0.1) }}>
          <div className="mb-4 flex items-center justify-between">
            <ColorCircle value={kiosk.color} />
            <div className="flex items-center gap-1.5">
              <Dot color={kiosk.status === 'active' ? 'success' : 'danger'} />
              <p className="text-sm font-500 text-neutral-900">
                {kiosk.status === 'active' ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
          <div className="flex h-full w-full flex-col justify-between">
            <div>
              <p className="text-xl font-500 text-neutral-900">{kiosk.name}</p>
              <p>{kiosk.description}</p>
            </div>
            <div className="flex w-full justify-between">
              <div
                className={cn(
                  'w-fit rounded-2xl px-3 py-1 text-sm font-500 text-neutral-600',
                  kioskState.color
                )}>
                {kioskState.label}
              </div>
              <div>
                <Popover
                  position="left"
                  menuPortalTarget={document.body}
                  options={[
                    {
                      label: 'Edit',
                      icon: 'edit-2-mono',
                      onClick: () => setEditKiosk(kiosk)
                    },
                    {
                      label: kiosk.status === 'active' ? 'Disable' : 'Enable',
                      icon: kiosk.status === 'active' ? 'cancel-mono' : 'check-mono',
                      onClick: () => toggleKioskStatusHandler()
                    },
                    {
                      label: 'Delete',
                      icon: 'trash-mono',
                      onClick: () => setDeleteKioskModal(kiosk.id)
                    }
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center justify-between px-1 text-sm">
          <p>Last active</p>
          <p className="text-xs text-neutral-600">{formatDate(kiosk.last_active)}</p>
        </div>
      </div>
      {!!deleteKioskModal && (
        <Confirm
          variant="danger"
          primaryBtnTxt="Delete"
          title="Delete Kiosk"
          message="Are you sure you want to delete Kiosk?"
          handleContinue={deleteKioskHandler}
          handleOpen={!!deleteKioskModal}
          handleClose={() => setDeleteKioskModal(null)}
        />
      )}
      {editKiosk && <EditKioskModal kiosk={kiosk} setKiosk={setEditKiosk} />}
    </>
  );
};

export default KioskCard;
